// LAB-ONLY candidate alternates for the Funny ("Bonk!") finisher — previewed
// on /vfx-lab for approval, NOT wired into the game. Same uniform contract as
// funny.frag.ts (uTime / uDuration / uAspect / uCenter) so an approved one can
// be swapped in by changing only the fragment source.

// Boing! — springy squash-and-stretch rings that re-hit like a dropped ball,
// with 5 sparkle stars orbiting on a flattened halo. Squash is applied by
// scaling UV space, so the rings visibly flatten/stretch on each hop.
export const FUNNY_BOING_FRAG = /* glsl */ `
  precision mediump float;
  varying vec2 vUv;
  uniform float uTime;
  uniform float uDuration;
  uniform vec2 uAspect;
  uniform vec2 uCenter;

  void main() {
    vec2 uv = ((vUv - uCenter) * 2.0) * uAspect;
    float t = clamp(uTime / uDuration, 0.0, 1.0);
    float tt = uTime;

    // Damped floor-bounce: |sin| re-hits, exp tames each successive hop.
    float bounce = abs(sin(tt * 6.0)) * exp(-2.6 * t);
    float s = 0.45 * bounce;
    vec2 q = vec2(uv.x / (1.0 + s), uv.y * (1.0 + s));
    float qr = length(q);

    float ring1 = 1.0 - smoothstep(0.0, 0.06, abs(qr - 0.42));
    float ring2 = 1.0 - smoothstep(0.0, 0.045, abs(qr - (0.62 + 0.05 * bounce)));
    float rings = ring1 + ring2 * 0.55;

    // 5 orbiting sparkle stars — cos(al*5) spikes read as cartoon stars.
    float stars = 0.0;
    for (int i = 0; i < 5; i++) {
      float fi = float(i);
      float oa = tt * 3.2 + fi * 1.2566371;
      vec2 sp = vec2(cos(oa), sin(oa) * 0.6) * (0.78 + 0.08 * bounce);
      vec2 d = uv - sp;
      float al = atan(d.y, d.x);
      float spike = 0.55 + 0.45 * cos(al * 5.0 + tt * 4.0);
      stars += exp(-length(d) * 24.0) * spike;
    }

    float env = smoothstep(0.0, 0.08, t) * (1.0 - smoothstep(0.78, 1.0, t));

    vec3 mint = vec3(0.063, 0.957, 0.545);
    vec3 sun = vec3(1.0, 0.9, 0.35);
    vec3 col = (mint * rings + sun * stars * 0.9) * env;
    float alpha = clamp((rings + stars) * env, 0.0, 1.0);
    gl_FragColor = vec4(col, alpha);
  }
`

// Splat! — a cartoon paint splat: a wobbly-edged blob pops in with overshoot,
// jiggles, and flings shrinking droplets ahead of its rim. Droplets are
// sector-hashed with mod() so the -x seam lands on a cell edge (no seam line).
export const FUNNY_SPLAT_FRAG = /* glsl */ `
  precision mediump float;
  varying vec2 vUv;
  uniform float uTime;
  uniform float uDuration;
  uniform vec2 uAspect;
  uniform vec2 uCenter;

  float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
  }

  void main() {
    vec2 uv = ((vUv - uCenter) * 2.0) * uAspect;
    float r = length(uv);
    float a = atan(uv.y, uv.x);
    float t = clamp(uTime / uDuration, 0.0, 1.0);
    float tt = uTime;

    // Pop-in with overshoot: sin(pi*x) peaks mid-pop then settles back to 1.
    float grow = smoothstep(0.0, 0.16, t);
    float over = 1.0 + 0.3 * sin(min(t / 0.16, 1.0) * 3.14159);
    float wob = 0.16 * sin(a * 7.0 + 1.7) + 0.1 * sin(a * 11.0 - tt * 2.5);
    float blobR = 0.5 * grow * over * (1.0 + wob);

    float fill = 1.0 - smoothstep(blobR - 0.015, blobR + 0.015, r);
    float rim = exp(-pow((r - blobR) * 22.0, 2.0));

    // Droplets flung ahead of the rim, one per sector, shrinking in flight.
    float cells = 10.0;
    float sa = (a / 6.2831853 + 0.5) * cells;
    float seg = mod(floor(sa), cells);
    float fr = fract(sa) - 0.5;
    float h = hash(vec2(seg, 5.0));
    float fly = smoothstep(0.05, 0.55, t);
    float dropDist = 0.3 + (0.45 + 0.5 * h) * fly;
    vec2 dp = vec2(fr * 0.63 * max(r, 0.1), r - dropDist);
    float drop = exp(-dot(dp, dp) * (240.0 + 500.0 * fly)) * step(0.15, t);

    float fade = 1.0 - smoothstep(0.7, 1.0, t);

    vec3 mint = vec3(0.063, 0.957, 0.545);
    vec3 col = (mint * (fill * 0.55 + drop) + mix(mint, vec3(1.0), 0.7) * rim) * fade;
    float alpha = clamp(fill * 0.55 + rim + drop, 0.0, 1.0) * fade;
    gl_FragColor = vec4(col, alpha);
  }
`

// Dizzy! — classic "seeing stars" KO: 4 white stars (plus motion-trail ghosts)
// orbiting a squashed halo over a faint hypno-swirl. Swirl uses sin(a*K) with
// integer K so it is continuous across the -x axis — no seam.
export const FUNNY_DIZZY_FRAG = /* glsl */ `
  precision mediump float;
  varying vec2 vUv;
  uniform float uTime;
  uniform float uDuration;
  uniform vec2 uAspect;
  uniform vec2 uCenter;

  void main() {
    vec2 uv = ((vUv - uCenter) * 2.0) * uAspect;
    float r = length(uv);
    float a = atan(uv.y, uv.x);
    float t = clamp(uTime / uDuration, 0.0, 1.0);
    float tt = uTime;

    float swirl = (0.5 + 0.5 * sin(a * 3.0 - r * 10.0 + tt * 4.5)) * exp(-1.4 * r);

    // 4 orbiting stars, each followed by a smaller, dimmer trail ghost.
    float stars = 0.0;
    for (int i = 0; i < 4; i++) {
      float fi = float(i);
      float oa = -tt * 3.8 + fi * 1.5707963;
      for (int j = 0; j < 2; j++) {
        float fj = float(j);
        float ang2 = oa + fj * 0.32;
        vec2 sp = vec2(cos(ang2) * 0.62, sin(ang2) * 0.24 + 0.04 * sin(tt * 2.0));
        vec2 d = uv - sp;
        float al = atan(d.y, d.x);
        float spike = 0.55 + 0.45 * cos(al * 5.0 - tt * 5.0);
        stars += exp(-length(d) * (18.0 + 8.0 * fj)) * spike * (1.4 - fj * 0.8);
      }
    }

    float env = smoothstep(0.0, 0.1, t) * (1.0 - smoothstep(0.8, 1.0, t));

    vec3 mint = vec3(0.063, 0.957, 0.545);
    vec3 col = (mint * swirl * 0.85 + vec3(1.0) * stars) * env;
    float alpha = clamp(swirl * 0.75 + stars, 0.0, 1.0) * env;
    gl_FragColor = vec4(col, alpha);
  }
`
